/**
 * Recursively collects File objects from a single FileSystemEntry (file or
 * directory), mutating the shared `files` accumulator array in place.
 * @param {FileSystemEntry} entry
 * @param {File[]} files
 * @returns {Promise<void>}
 */
async function collectFilesFromEntry(entry, files) {
    if (entry.isFile) {
        const file = await new Promise(function resolveFile(resolve, reject) { entry.file(resolve, reject); });
        files.push(file);
        return;
    }
    if (entry.isDirectory) {
        const reader = entry.createReader();
        let batch;
        do {
            batch = await new Promise(function resolveBatch(resolve, reject) { reader.readEntries(resolve, reject); });
            for (const child of batch) {
                await collectFilesFromEntry(child, files);
            }
        } while (batch.length > 0); // readEntries can require multiple calls per spec, doesn't always return everything at once
    }
}

/**
 * Reads a drop event's DataTransfer into a flat File array, recursing into
 * dropped folders via the webkitGetAsEntry FileSystemEntry API. Falls back
 * to dataTransfer.files (no folder recursion, isFolder always false) if the
 * browser doesn't support webkitGetAsEntry (older Firefox).
 *
 * isFolder is true if any TOP-LEVEL dropped item was a directory entry —
 * NOT based on File.webkitRelativePath, which (unlike the webkitdirectory
 * input case) is not reliably populated for files obtained via
 * FileSystemFileEntry.file() during drag-and-drop.
 * @param {DataTransfer} dataTransfer
 * @returns {Promise<{files: File[], isFolder: boolean}>}
 */
export async function readDroppedFiles(dataTransfer) {
    const items = Array.from(dataTransfer.items || []);
    const entries = items.map(function toEntry(item) { return item.webkitGetAsEntry?.(); }).filter(Boolean);
    if (entries.length === 0) {
        return { files: Array.from(dataTransfer.files || []), isFolder: false };
    }
    const isFolder = entries.some(function isDirectoryEntry(entry) { return entry.isDirectory; });
    const files = [];
    for (const entry of entries) {
        await collectFilesFromEntry(entry, files);
    }
    return { files, isFolder };
}
